/**
 * The account screen: how you signed in, and how you leave.
 *
 * Deleting the account still asks for DELETE to be typed, in its own window;
 * the warning is on this screen, above the button, not inside a dialog the
 * gardener has to open to find out what they are agreeing to.
 *
 * Used by: UserProfile (?s=account).
 *
 * How they sign in comes from Firebase's providerData: a line for each of
 * Google, Facebook and email and password the account has (one linked to
 * another has both), in the order Firebase lists them. Nothing is said for a
 * sign-in it does not know (mock auth has none).
 * Words (en/es): profile.account.connectedGoogle, connectedFacebook,
 * connectedEmail, deleteDescription, delete; profile.index.accountRow.
 *
 * @module AccountCard
 */

import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { ProfileScreen } from './ProfileScreen';

/** Firebase's sign-in provider ids, and the line that says each. */
const PROVIDER_LINES: Record<string, string> = {
  'google.com': 'profile.account.connectedGoogle',
  'facebook.com': 'profile.account.connectedFacebook',
  password: 'profile.account.connectedEmail',
};

/**
 * The profile's Account screen: how they signed in, and deleting the account.
 *
 * @param props - open: whether the screen is showing; onClose: closes it.
 */
export const AccountScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const { currentUser } = useAuth();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  /** The words for each way this account signs in, once each, as Firebase lists them. */
  const signIns = [...new Set((currentUser?.providerData ?? []).map((info) => PROVIDER_LINES[info?.providerId ?? '']).filter(Boolean))];

  return (
    <ProfileScreen open={open} title={t('profile.index.accountRow')} onClose={onClose}>
      {signIns.map((key) => <p key={key} className="text-sm text-muted-foreground">{t(key)}</p>)}
      <div className="space-y-3 rounded-lg border bg-card p-4">
        <p className="text-sm text-muted-foreground">{t('profile.account.deleteDescription')}</p>
        <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
          {t('profile.account.delete')}
        </Button>
      </div>
      <DeleteAccountDialog isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen} />
    </ProfileScreen>
  );
};
